import { useMemo, useState } from "react";
import { parseISO, isBefore, addDays } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { statusMeta, priorityMeta, trackMeta } from "@/lib/constants";
import { ListTodo, AlertTriangle, ShieldAlert, CheckCircle2, CalendarClock } from "lucide-react";

const isDone = (t) => t.status === "done" || (t.percent_complete || 0) >= 100;

export const STAT_FILTERS = {
  all: { label: "All tasks", tint: "#0044FF", icon: ListTodo, match: () => true },
  critical: { label: "Critical priority", tint: "#EF4444", icon: AlertTriangle, match: (t) => t.priority === "critical" && !isDone(t) },
  at_risk: { label: "At risk / Jeopardy", tint: "#F59E0B", icon: ShieldAlert, match: (t) => ["at_risk", "jeopardy"].includes(t.track_status) && !isDone(t) },
  complete: { label: "Completed", tint: "#10B981", icon: CheckCircle2, match: (t) => isDone(t) },
};

const TIMEFRAMES = [
  { key: "any", label: "Any time" },
  { key: "overdue", label: "Overdue" },
  { key: "7", label: "Due in 7 days" },
  { key: "14", label: "Due in 14 days" },
  { key: "30", label: "Due in 30 days" },
  { key: "90", label: "Due in 90 days" },
];

function inTimeframe(t, tf) {
  if (tf === "any") return true;
  if (!t.end_date) return false;
  const end = parseISO(t.end_date);
  const now = new Date();
  if (tf === "overdue") return isBefore(end, now) && !isDone(t);
  return !isBefore(end, now) && isBefore(end, addDays(now, Number(tf)));
}

export default function StatTasksDialog({ open, onOpenChange, filterKey, tasks, phaseColors, onOpenTask, config }) {
  const [timeframe, setTimeframe] = useState("any");
  const cfg = config || STAT_FILTERS[filterKey] || STAT_FILTERS.all;
  const Icon = cfg.icon;

  const filtered = useMemo(
    () => tasks.filter(cfg.match).filter((t) => inTimeframe(t, timeframe)),
    [tasks, cfg, timeframe]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stat-tasks-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: cfg.tint }} /> {cfg.label}
            <span className="text-sm font-normal text-muted-foreground">({filtered.length})</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-44 h-9" data-testid="stat-timeframe-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEFRAMES.map((tf) => <SelectItem key={tf.key} value={tf.key} data-testid={`stat-timeframe-${tf.key}`}>{tf.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10" data-testid="stat-tasks-empty">No tasks match this filter and timeframe.</p>
        ) : (
          <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
            {filtered.map((t) => {
              const sm = statusMeta(t.status);
              const pm = priorityMeta(t.priority);
              const tm = trackMeta(t.track_status);
              return (
                <button key={t.id} onClick={() => onOpenTask(t)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/30 text-left" data-testid={`stat-task-${t.id}`}>
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: (phaseColors && phaseColors[t.phase]) || cfg.tint }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.phase} · {t.assignee || "Unassigned"}{t.end_date ? ` · due ${t.end_date}` : ""}</p>
                  </div>
                  {t.track_status && t.track_status !== "on_track" && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded shrink-0" style={{ color: tm.color, background: `${tm.color}1a` }}>{tm.label}</span>
                  )}
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded shrink-0" style={{ color: pm.color, background: `${pm.color}1a` }}>{pm.label}</span>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded shrink-0" style={{ color: sm.color, background: `${sm.color}1a` }}>{sm.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
